import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Download, KeyRound, ShieldAlert, Star, Tags, Trash2 } from "lucide-react";
import { useEffect, useRef, useState } from "react";

import ScreenHeader from "../components/ScreenHeader";
import UploadSheet from "../components/UploadSheet";
import { errorMessageOf, isUserCancellation, signedInWithPasskey, useAuth } from "../lib/auth";
import { useMediaDownload } from "../lib/media/download";
import { fetchExportList } from "../lib/media/export";
import { attachTag, createTag } from "../lib/media/tags";
import { uploadMedia, withScreenAwake } from "../lib/media/upload";
import { useMediaTasks } from "../lib/mediaTasks";
import { queryKeys } from "../lib/queryKeys";
import { supabase } from "../lib/supabase";
import { registerPasskey } from "../lib/passkey";
import { scrollToTop } from "../lib/scroll";
import { useToast } from "../lib/toast";
import Favorites from "./Favorites";
import TagManager from "./TagManager";
import Timeline from "./Timeline";
import Trash from "./Trash";

interface Profile {
  id: string;
  couple_id: string | null;
  display_name: string;
}

const ACCEPT = "image/*,video/*,.heic,.heif";

export default function Album() {
  const { session } = useAuth();
  const queryClient = useQueryClient();
  const fileInput = useRef<HTMLInputElement | null>(null);

  const toast = useToast();
  const download = useMediaDownload();
  const [showTrash, setShowTrash] = useState(false);
  const [showTags, setShowTags] = useState(false);
  const [showFavorites, setShowFavorites] = useState(false);
  // ピッカーから受け取った直後の、まだ送っていないファイル
  const [staged, setStaged] = useState<File[] | null>(null);
  const [registering, setRegistering] = useState(false);
  const [passkeyPromptClosed, setPasskeyPromptClosed] = useState(false);
  // ピッカーを開いてからファイルが届くまでの空白時間。
  // iOS はこの間に素材を書き出しており、その進行を一切表示しない。
  // 表示は出さず、追加ボタンを押せなくすることで二重に開くのだけ防ぐ。
  const [waitingForPicker, setWaitingForPicker] = useState(false);
  // 選択モード。PCのヘッダーとタイムラインの丸ボタンの両方から切り替える
  const [selectMode, setSelectMode] = useState(false);
  // 進捗は画面を移っても見えるようアプリ全体で持つ(表示は ScreenHeader)
  const { upload: progress, setUpload: setProgress } = useMediaTasks();

  const userId = session?.user.id;

  const profile = useQuery({
    queryKey: queryKeys.profile(userId),
    queryFn: async (): Promise<Profile | null> => {
      // RLS の profiles_select は「同じカップルの全員」を返すため、
      // 絞り込まないと相手の行も混ざり maybeSingle() が例外になる。
      // ポリシー側は相手の表示名を出すために必要なので、絞り込みはクエリ側で行う。
      const { data, error: queryError } = await supabase
        .from("profiles")
        .select("id, couple_id, display_name")
        .eq("id", userId ?? "")
        .maybeSingle();
      if (queryError) throw queryError;
      return data;
    },
    enabled: userId !== undefined,
  });

  useEffect(() => {
    const input = fileInput.current;
    if (input === null) return;
    const onCancel = () => {
      setWaitingForPicker(false);
    };
    input.addEventListener("cancel", onCancel);
    return () => {
      input.removeEventListener("cancel", onCancel);
    };
  }, []);

  /**
   * マジックリンクで入った人に、その端末へパスキーを生やしてもらう。
   * マジックリンクは常用の入口ではなく、ここへ辿り着くための道(→ ADR-0016)。
   */
  async function addPasskey() {
    setRegistering(true);
    try {
      await registerPasskey();
      setPasskeyPromptClosed(true);
      toast("パスキーを登録しました。");
    } catch (e) {
      if (!isUserCancellation(e)) toast(errorMessageOf(e), "error");
    } finally {
      setRegistering(false);
    }
  }

  function openPicker() {
    setWaitingForPicker(true);
    fileInput.current?.click();
  }

  /** 送信済みのものにタグを付ける。既にあったぶんにも付ける(付けるつもりで選んでいる) */
  async function applyTags(mediaIds: string[], tagNames: string[]) {
    if (mediaIds.length === 0 || tagNames.length === 0) return;
    for (const name of tagNames) {
      const tag = await createTag(name);
      await attachTag(mediaIds, tag.id);
    }
  }

  async function startUpload(list: File[], tagNames: string[]) {
    setStaged(null);
    // 進み具合は画面の上に出る。下を見ていると何も起きていないように見える
    scrollToTop();

    // エラーの本文はユーザーには出さない(件数だけ伝える)。詳細は開発者ツールで
    let failedCount = 0;
    let tagsFailed = false;
    const mediaIds: string[] = [];
    let uploaded = 0;
    let duplicates = 0;

    // 画面がロックされるとiOSはJSを止める。アップロード中だけ消灯を抑える
    await withScreenAwake(async () => {
      for (const [index, file] of list.entries()) {
        setProgress({
          done: index,
          total: list.length,
          name: file.name,
          phase: "preparing",
          ratio: 0,
        });
        try {
          const outcome = await uploadMedia(file, ({ phase, ratio }) => {
            setProgress({ done: index, total: list.length, name: file.name, phase, ratio });
          });
          mediaIds.push(outcome.mediaId);
          if (outcome.status === "duplicate") duplicates += 1;
          else uploaded += 1;
        } catch (e) {
          failedCount += 1;
          console.error(`${file.name} のアップロードに失敗`, e);
        }
      }

      try {
        await applyTags(mediaIds, tagNames);
      } catch (e) {
        tagsFailed = true;
        console.error("タグの登録に失敗", e);
      }
    });

    setProgress(null);
    await queryClient.invalidateQueries({ queryKey: queryKeys.timelines });
    await queryClient.invalidateQueries({ queryKey: queryKeys.monthCounts });
    await queryClient.invalidateQueries({ queryKey: queryKeys.tags });

    if (failedCount > 0 || tagsFailed) {
      const lines = [];
      if (failedCount > 0) lines.push(`${String(failedCount)}件のアップロードに失敗しました。`);
      if (tagsFailed) lines.push("タグの登録に失敗しました。");
      toast(lines.join("\n"), "error");
    } else if (uploaded === 0) {
      toast(`${String(duplicates)}件はすべて追加済みのため、スキップしました。`);
    } else {
      const skipped =
        duplicates > 0 ? `\n${String(duplicates)}件は追加済みのため、スキップしました。` : "";
      toast(`${String(uploaded)}件を追加しました。${skipped}`);
    }
  }

  const token = session?.access_token;
  const needsPasskey = !passkeyPromptClosed && token !== undefined && !signedInWithPasskey(token);

  const coupleMissing =
    profile.isSuccess && (profile.data === null || profile.data.couple_id === null);
  const profileFailed = profile.isError;

  const uploadDisabled = coupleMissing || progress !== null || waitingForPicker || staged !== null;

  return (
    <>
      <ScreenHeader
        navKey="album"
        menu={[
          {
            label: "タグ管理",
            icon: Tags,
            onSelect: () => {
              setShowTags(true);
            },
          },
          {
            label: "お気に入り",
            icon: Star,
            onSelect: () => {
              setShowFavorites(true);
            },
          },
          {
            label: "ゴミ箱",
            icon: Trash2,
            onSelect: () => {
              setShowTrash(true);
            },
          },
          {
            // 端末を変えるときや、この仕組みをやめるときの出口。
            // データを人質に取らないようにしておく。
            // 選んだぶんだけ落とす道は選択モードの操作バーにある(→ Timeline)
            label: "まとめてダウンロード",
            icon: Download,
            onSelect: () => {
              void download(fetchExportList);
            },
          },
        ]}
      />

      <input
        ref={fileInput}
        type="file"
        accept={ACCEPT}
        multiple
        hidden
        onChange={(e) => {
          setWaitingForPicker(false);
          const list = e.target.files === null ? [] : [...e.target.files];
          if (list.length > 0) setStaged(list);
          e.target.value = "";
        }}
      />

      <div className="flex flex-col gap-3 px-4 pt-3">
        {profileFailed && (
          <p
            role="alert"
            className="flex gap-2 rounded-xl border border-[var(--color-line)] bg-white p-3 text-sm"
          >
            <ShieldAlert className="size-5 shrink-0 text-[var(--color-plum)]" aria-hidden />
            <span>
              プロフィールを取得できませんでした。
              <span className="mt-1 block text-xs text-[var(--color-sub)]">
                {errorMessageOf(profile.error)}
              </span>
            </span>
          </p>
        )}

        {needsPasskey && (
          <div className="flex flex-col gap-2 rounded-xl border border-[var(--color-line)] bg-white p-3 text-sm">
            <p className="flex gap-2">
              <KeyRound className="size-5 shrink-0 text-[var(--color-teal)]" aria-hidden />
              <span>
                メールのリンクで入りました。
                <span className="mt-1 block text-xs text-[var(--color-sub)]">
                  この端末にパスキーを登録しておくと、次回からメールなしで入れます。
                </span>
              </span>
            </p>
            <div className="flex justify-end gap-2">
              <button
                type="button"
                onClick={() => {
                  setPasskeyPromptClosed(true);
                }}
                className="rounded-lg px-3 py-1.5 text-xs text-[var(--color-sub)]"
              >
                あとで
              </button>
              <button
                type="button"
                onClick={() => void addPasskey()}
                disabled={registering}
                className="rounded-lg bg-[var(--color-teal)] px-3 py-1.5 text-xs text-white disabled:opacity-50"
              >
                {registering ? "登録中..." : "登録する"}
              </button>
            </div>
          </div>
        )}

        {coupleMissing && (
          <p className="flex gap-2 rounded-xl border border-[var(--color-line)] bg-white p-3 text-sm">
            <ShieldAlert className="size-5 shrink-0 text-[var(--color-plum)]" aria-hidden />
            <span>
              カップルに紐付いていないため、まだ写真を保存できません(README
              のセットアップ手順を参照)。
            </span>
          </p>
        )}
      </div>

      <Timeline
        onPickFiles={openPicker}
        uploadDisabled={uploadDisabled}
        selectMode={selectMode}
        onSelectModeChange={setSelectMode}
      />

      {staged !== null && (
        <UploadSheet
          files={staged}
          onRemove={(index) => {
            setStaged((current) => {
              const next = (current ?? []).filter((_, i) => i !== index);
              return next.length === 0 ? null : next;
            });
          }}
          onCancel={() => {
            setStaged(null);
          }}
          onStart={(tagNames) => void startUpload(staged, tagNames)}
        />
      )}

      {showFavorites && (
        <Favorites
          onClose={() => {
            setShowFavorites(false);
          }}
        />
      )}

      {showTags && (
        <TagManager
          onClose={() => {
            setShowTags(false);
          }}
        />
      )}

      {showTrash && (
        <Trash
          onClose={() => {
            setShowTrash(false);
          }}
        />
      )}
    </>
  );
}
